import React, { ReactNode, memo, PropsWithChildren, useContext, useEffect, useRef, useState } from 'react';
import { Button } from '@pokemon/design-v2.button'
import { useTranslation } from 'react-i18next'
import { LoadingContext, LoadingContextValue } from '@pokemon/design.ui.loading-spinner'
import Axios from 'axios';
import LinkifyIt from 'linkify-it';

import './document-page.css'

export enum DocumentType {
  TermsOfUse = 'TERMS_OF_USE',
  PrivacyNotice = 'PRIVACY_NOTICE'
}

export interface DocumentPageProps {
  /**
   * a function that calls when user clicks on the Cancel button 
   */
  onCancel: React.MouseEventHandler<HTMLButtonElement & HTMLAnchorElement>;
  /**
   * a function that calls when user clicks on the Accept button 
   */
  onAccept: React.MouseEventHandler<HTMLButtonElement & HTMLAnchorElement>;
  /**
   * an enum to determine to show Terms of Use or Privacy Notice Document 
   */
  documentType: DocumentType;
  /**
   * a function called when an error occurs when fetching document data 
   */
  setError: (event: string) => void;
  /**
   * the URL of an API that returns the document you want displayed. Must return JSON of this format
   * [
   *    {
   *      title: string
   *      paragraph: string
   *    },
   * ]
   */
  documentURL: string
};

export const Card = (props: PropsWithChildren<{}>) => {
  const documentCard = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (documentCard.current) {
      documentCard.current.scrollTop = 0
    }
  }, [props.children])

  return (
    <div ref={documentCard} className="tpci-card" data-testid="document-card">
      {props.children}
    </div>
  )
}

interface DocumentArticle {
  title: string
  paragraph: string
}

export function DocumentPage ({ documentType, onCancel, onAccept, setError, documentURL }: DocumentPageProps) {
  const [documentText, setDocumentText] = useState('')
  const { t } = useTranslation()
  const { setLoading } = useContext<LoadingContextValue>(LoadingContext)

  useEffect(() => {
    setLoading(true)
    Axios.get(documentURL).then(res => {
      // checking if terms of use and privacy notice documents can accessed
      // if successful, API returns document in JSON format
      let documentText = ''
      const documentBody: DocumentArticle[] = res.data
      documentBody.forEach(article => {
        documentText += article.title + '\n' + article.paragraph + '\n'
      })
      setDocumentText(documentText)
    }).catch((error: string) => {
      setError(error)
    }).finally(() => {
      setLoading(false)
    })
  }, [documentType])

  const documentTypeId = documentType === DocumentType.TermsOfUse ? 'terms-of-use' : 'privacy-notice';
  const documentHeader = documentType === DocumentType.TermsOfUse ? t('terms_of_use_header', 'Please review and accept Terms of Use') : t('privacy_notice_header', 'Please review and acknowledge the Privacy Notice');

  return (
    <>
      <div className='document-header' data-testid={`${documentTypeId}-header`}>{documentHeader}</div>
      <Card>
        <LinkifyText text={documentText}/>
      </Card>
      <div className='document-buttons'>
        <Button style={{ width: '45%' }}
          data-testid={`${documentTypeId}-cancel-button`}
          text={t('document_cancel', 'Cancel')}
          variant={'outlined'}
          onClick={onCancel}/>
        <Button style={{ width: '45%' }}
          data-testid={`${documentTypeId}-submit-button`}
          text={t('document_accept', 'Accept')}
          onClick={onAccept}/>
      </div>
    </>
  )
}

const linkify = new LinkifyIt()

interface LinkifyTextProps {
  text: string
}

const LinkifyText = memo(({ text }: LinkifyTextProps) => {
  const matches = linkify.match(text)
  if (!matches) {
    return <>{text}</>
  }
  let elements: ReactNode[] = []
  let lastIndex = 0
  for (const match of matches) {
    if (match.index > lastIndex) {
      elements.push(text.substring(lastIndex, match.index))
    }
    elements.push(<a className='link' key={match.index} href={match.url}>{match.text}</a>)
    lastIndex = match.lastIndex
  }
  if (text.length > lastIndex) {
    elements.push(text.substring(lastIndex))
  }
  return <>{elements}</>
})
